import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { clsx } from "clsx";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { clampCompare, parseCompareParam } from "../lib/compare";
import { dataset } from "../lib/dataset";
import { factionLinkSearch } from "../lib/faction-selection";
import { linkFrame } from "../lib/frame";
import { searchUnits } from "../lib/search";
import { type UnitLinkSearch, unitLinkSearch } from "../lib/unit-link";
import { RACE_META, UNIT_KIND_LABELS } from "../lib/vocabulary";
import { ArtImage } from "./art-image";
import { Chip } from "./chip";

// The WHOLE Roster, mapped once into what the matcher and the result rows need. Class labels
// are the game's own (UNIT_KIND_LABELS), so "frigate" finds Advent frigates whose names never
// say the word.
//
// It is the whole Roster under every Faction Selection, on every route (ADR 0010, Issue 36):
// the search is the one surface the selection must never narrow, because a search box that
// silently hides matches is the worst kind of filter and the rows already carry a Race chip.
// Exported so a test can pin that — the corpus is the dataset's own list, and the matcher
// below takes a corpus and a query and nothing else, so no page can slip a selection in.
export const SEARCH_CORPUS = dataset.units.map((unit) => ({
  id: unit.id,
  name: unit.name,
  kindLabel: UNIT_KIND_LABELS[unit.kind],
  race: unit.race,
  hudIcon: unit.art.hudIcon,
}));

/** What a result's link carries off the palette: the active stat frame, the site-wide Faction
 * Selection and the pending compare set — the Issue 08 contract, so palette navigation keeps
 * everything a roster row's link keeps. Routes without the params (e.g. /tech) read as the
 * defaults, which serialize back to a canonical bare URL. On /compare the set lives in
 * `?units`, so both spellings are read (a review round caught the palette dropping the set
 * when opened from the comparison itself).
 *
 * This is the ONLY place the selection touches the search: it rides out on the link, and it
 * never reaches the corpus above.
 *
 * The frame is read the way the nav tabs read it (linkFrame): the palette is a shell-level
 * link too, so a comparison's per-column slots ride nothing rather than landing on a
 * single-unit page as a token it cannot honour (review 2026-09-08). What the link then
 * carries is what EVERY unit-level link carries (lib/unit-link.ts); only the reading of the
 * set — `compare` or /compare's `units` — is the palette's own. */
export function paletteLinkSearch(search: PaletteRouteSearch): UnitLinkSearch {
  return unitLinkSearch(
    linkFrame(search),
    factionLinkSearch(search),
    clampCompare(parseCompareParam(search.compare ?? search.units)),
  );
}

interface PaletteRouteSearch {
  faction?: string;
  tech?: string;
  corruption?: string;
  compare?: string;
  units?: string;
}

/** Unit search as a command palette (Issue 06): one overlay for every viewport — the
 * header has no room for an inline input at phone width. The trigger button, "/" or
 * Ctrl+K opens it; typing matches names and Class labels; ArrowUp/Down move the active
 * row, Enter opens the active unit's page, Escape closes. The query is transient state
 * by design — it never touches the URL contract (ADR 0002). */
export function UnitSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  // Read loosely: the palette renders in the shell, above any one route.
  const routeSearch = useSearch({ strict: false }) as PaletteRouteSearch;
  const frameSearch = paletteLinkSearch(routeSearch);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  // The whole Roster, always: the query is the only thing that narrows it (ADR 0010).
  const results = searchUnits(SEARCH_CORPUS, query);

  // Stable so the modal effect below subscribes once per open, not once per keystroke.
  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    // A closing dialog hands focus back to its trigger — otherwise it drops to <body>
    // and a keyboard user loses their place.
    triggerRef.current?.focus();
  }, []);

  // "/" and Ctrl/Cmd+K open the palette from anywhere, unless the user is typing in some
  // other field. The K is lowercased so Caps Lock cannot break the shortcut.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target?.isContentEditable ?? false);
      if (
        (event.key === "/" && !typing) ||
        (event.key.toLowerCase() === "k" && (event.ctrlKey || event.metaKey))
      ) {
        event.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // While open, the palette is modal: Escape closes it no matter where focus sits
  // (capture + stopPropagation, so a popover layered beneath does not also dismiss),
  // and the page cannot scroll away behind the overlay.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [open, close]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (results.length > 0) setActiveIndex((index) => Math.min(index + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      const unit = results[activeIndex];
      if (unit) {
        // preventDefault, or the keydown's default action activates the trigger button
        // that close() just refocused and the palette instantly reopens (pre-existing
        // since Issue 06; caught here because SPA-flow testing stayed on the page).
        event.preventDefault();
        close();
        void navigate({ to: "/unit/$unitId", params: { unitId: unit.id }, search: frameSearch });
      }
    }
  }

  return (
    <>
      {/* From sm up the trigger grows into the header's free space and reads like a
          search field (label left, shortcut hint right); below sm it stays the compact
          icon — there is no space to take. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search units"
        className="flex cursor-pointer items-center gap-1.5 rounded border border-line px-2 py-1 font-mono text-[12.5px] text-ink-3 transition-colors hover:border-line-strong hover:text-ink-2 sm:flex-1 sm:max-w-52"
      >
        <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none">
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
          <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden rounded border border-line px-1 text-[10.5px] text-muted-2 sm:ml-auto sm:inline">
          /
        </kbd>
      </button>
      {open && (
        // Tab is trapped here: the input is the dialog's only tab stop (option rows are
        // arrow-key territory per the combobox pattern), so Tab must not walk focus into
        // the page behind the backdrop.
        // biome-ignore lint/a11y/noStaticElementInteractions: a keydown fence for the modal, not a control; the dialog semantics live on the panel below
        <div
          className="fixed inset-0 z-40"
          onKeyDown={(event) => {
            if (event.key === "Tab") event.preventDefault();
          }}
        >
          {/* A real button so the click-to-close backdrop needs no a11y suppressions;
              Escape is the keyboard path. */}
          <button
            type="button"
            aria-label="Close search"
            tabIndex={-1}
            onClick={close}
            className="absolute inset-0 cursor-default bg-black/60"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Unit search"
            className="absolute inset-x-4 top-14 mx-auto max-w-md rounded-lg border border-line-strong bg-surface shadow-[0_16px_48px_rgba(0,0,0,0.6)]"
          >
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onInputKeyDown}
              placeholder="Search units by name or class"
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls={results.length > 0 ? listId : undefined}
              aria-activedescendant={results.length > 0 ? `${listId}-${activeIndex}` : undefined}
              aria-autocomplete="list"
              aria-label="Search units"
              className="w-full rounded-t-lg border-b border-line bg-transparent px-4 py-3 text-[15px] text-ink placeholder:text-placeholder focus:outline-none"
            />
            {results.length > 0 ? (
              <div id={listId} role="listbox" aria-label="Matching units" className="py-1">
                {results.map((unit, index) => (
                  <Link
                    key={unit.id}
                    to="/unit/$unitId"
                    params={{ unitId: unit.id }}
                    search={frameSearch}
                    role="option"
                    id={`${listId}-${index}`}
                    aria-selected={index === activeIndex}
                    tabIndex={-1}
                    onClick={close}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={clsx(
                      "flex items-center gap-3 px-4 py-2",
                      index === activeIndex && "bg-raised",
                    )}
                  >
                    <span className="flex h-6 w-12 shrink-0 items-center justify-center">
                      <ArtImage
                        asset={unit.hudIcon}
                        alt=""
                        className="max-h-6 max-w-12 object-contain"
                      />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] text-ink">
                      {unit.name}
                    </span>
                    {/* Race identity outside its own column is the chip treatment (design
                        language amendment) — and the visible text is what screen readers
                        announce, so race is not sigil-color-only here. */}
                    <span className="flex shrink-0 items-center gap-1.5">
                      {/* A shade smaller and tighter than the shared box, because the
                          palette row is denser than a facet row. */}
                      <Chip
                        label={RACE_META[unit.race].label}
                        className="gap-1 text-[10.5px]!"
                        sigil={
                          <span
                            aria-hidden
                            className={clsx("text-[8px]", RACE_META[unit.race].textClass)}
                          >
                            {RACE_META[unit.race].sigil}
                          </span>
                        }
                      />
                      <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
                        {unit.kindLabel}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p role="status" className="px-4 py-3 text-[13px] text-muted-2">
                {query.trim() === ""
                  ? "Type a unit name or class. Enter opens the top match."
                  : `No units match "${query.trim()}".`}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
